// Email validation
// export const validateEmail = (email: string): boolean => {
//   if (!email) return false;
//   // eslint-disable-next-line no-useless-escape
//   const result =
//     /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
//   return result.test(email);
// };
export const validateEmail = (email: string): boolean => {
  if (!email) return false;

  // Common personal email domains to reject
  const personalDomains = [
    "gmail.com",
    "yahoo.com",
    "hotmail.com",
    "outlook.com",
    "aol.com",
    "icloud.com",
    "protonmail.com",
    "gmx.com",
    "mail.com",
    "yandex.com",
    "qq.com",
    "163.com",
    "live.com",
    "msn.com",
    "me.com",
    "zoho.com",
    "gmail.co.uk",
    "yahoo.co.uk",
    "hotmail.co.uk",
  ];

  // eslint-disable-next-line no-useless-escape
  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  // First validate basic email format
  if (!emailRegex.test(email)) {
    return false;
  }

  // Extract domain from email
  const domain = email.toLowerCase().split("@")[1];

  // Reject if it's a known personal email domain
  if (personalDomains.includes(domain)) {
    return false;
  }

  // Additional checks for corporate emails
  const corporateIndicators = [
    /\.(com|org|net|co\.[a-z]{2}|[a-z]{2,})$/i, // Standard TLDs
    /^[a-z0-9-]+\.(com|org|net|io|ai|co|tech|app|dev)$/i, // Company domain patterns
  ];

  // Check if domain looks like a corporate domain
  const isCorporateDomain = corporateIndicators.some((indicator) =>
    indicator.test(domain)
  );

  return isCorporateDomain;
};

// Alternative version if you want to be more restrictive
export const validateCorporateEmailStrict = (email: string): boolean => {
  if (!email) return false;

  // eslint-disable-next-line no-useless-escape
  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  if (!emailRegex.test(email)) {
    return false;
  }

  const domain = email.toLowerCase().split("@")[1];

  // Strict personal domains to block
  const strictPersonalDomains = [
    "gmail.com",
    "yahoo.com",
    "hotmail.com",
    "outlook.com",
    "aol.com",
    "icloud.com",
    "protonmail.com",
    "gmx.com",
    "mail.com",
    "yandex.com",
  ];

  // Block common personal domains
  if (strictPersonalDomains.includes(domain)) {
    return false;
  }

  // Allow only specific TLDs commonly used by businesses
  const corporateTLDs =
    /\.(com|org|net|io|ai|co\.[a-z]{2}|[a-z]{2}|tech|app|dev|cloud|digital)$/i;

  return corporateTLDs.test(domain);
};

// URL validation
export const validateURL = (url: string): boolean => {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

// Phone number validation (supports various formats)
export const validatePhone = (phone: string): boolean => {
  if (!phone) return false;
  // Remove spaces, dashes, parentheses, and plus signs for validation
  const cleaned = phone.replace(/[\s\-\(\)\+]/g, "");
  // Check if it contains only digits and has reasonable length (7-15 digits)
  return /^\d{7,15}$/.test(cleaned);
};

// Required field validation
export const validateRequired = (
  value: string | number | undefined | null
): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
};

// Minimum length validation
export const validateMinLength = (
  value: string,
  minLength: number
): boolean => {
  if (!value) return false;
  return value.trim().length >= minLength;
};

// Maximum length validation
export const validateMaxLength = (
  value: string,
  maxLength: number
): boolean => {
  if (!value) return true; // Empty is handled by required validation
  return value.trim().length <= maxLength;
};

// Password validation (at least 8 characters, contains letter and number)
export const validatePassword = (password: string): boolean => {
  if (!password) return false;
  if (password.length < 8) return false;
  // At least one letter and one number
  return /[A-Za-z]/.test(password) && /[0-9]/.test(password);
};

// Strong password validation (8+ chars, uppercase, lowercase, number, special char)
export const validateStrongPassword = (password: string): boolean => {
  if (!password) return false;
  if (password.length < 8) return false;
  // At least one uppercase, one lowercase, one number, and one special character
  return (
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[!@#$%^&*(),.?":{}|<>]/.test(password)
  );
};

// Number validation
export const validateNumber = (value: string | number): boolean => {
  if (value === "" || value === null || value === undefined) return false;
  return !isNaN(Number(value));
};

// Positive number validation
export const validatePositiveNumber = (value: string | number): boolean => {
  if (!validateNumber(value)) return false;
  return Number(value) > 0;
};

// Integer validation
export const validateInteger = (value: string | number): boolean => {
  if (!validateNumber(value)) return false;
  return Number.isInteger(Number(value));
};

// Date validation
export const validateDate = (date: string): boolean => {
  if (!date) return false;
  const dateObj = new Date(date);
  return dateObj instanceof Date && !isNaN(dateObj.getTime());
};

// Future date validation
export const validateFutureDate = (date: string): boolean => {
  if (!validateDate(date)) return false;
  return new Date(date) > new Date();
};

// Past date validation
export const validatePastDate = (date: string): boolean => {
  if (!validateDate(date)) return false;
  return new Date(date) < new Date();
};

// Date range validation (end date after start date)
export const validateDateRange = (
  startDate: string,
  endDate: string
): boolean => {
  if (!validateDate(startDate) || !validateDate(endDate)) return false;
  return new Date(endDate) >= new Date(startDate);
};

// Alphanumeric validation
export const validateAlphanumeric = (value: string): boolean => {
  if (!value) return false;
  return /^[A-Za-z0-9]+$/.test(value);
};

// Name validation (letters, spaces, hyphens, apostrophes)
export const validateName = (name: string): boolean => {
  if (!name) return false;
  return /^[A-Za-z\s\-']+$/.test(name.trim());
};

// Job title validation (letters, spaces, hyphens, apostrophes, parentheses - no numbers)
export const validateJobTitle = (title: string): boolean => {
  if (!title) return false;
  // Must not contain numbers
  if (/\d/.test(title.trim())) return false;
  // Allow letters, spaces, hyphens, apostrophes, parentheses, and periods
  return /^[A-Za-z\s\-'().,]+$/.test(title.trim());
};

// Question validation (only letters and spaces, multiple words required, no numbers or special characters)
export const validateQuestion = (question: string): boolean => {
  if (!question) return false;
  const trimmed = question.trim();
  // Must contain only letters and spaces
  if (!/^[A-Za-z\s]+$/.test(trimmed)) return false;
  // Must not contain numbers
  if (/\d/.test(trimmed)) return false;
  // Must have at least 2 words
  const words = trimmed.split(/\s+/).filter((word) => word.length > 0);
  return words.length >= 2;
};

// Credit card validation (Luhn algorithm)
export const validateCreditCard = (cardNumber: string): boolean => {
  if (!cardNumber) return false;
  const cleaned = cardNumber.replace(/\s/g, "");
  if (!/^\d{13,19}$/.test(cleaned)) return false;

  let sum = 0;
  let isEven = false;
  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned[i]);
    if (isEven) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    isEven = !isEven;
  }
  return sum % 10 === 0;
};

// ZIP/Postal code validation (basic - 5 digits or 5+4 format)
export const validateZipCode = (zipCode: string): boolean => {
  if (!zipCode) return false;
  return /^\d{5}(-\d{4})?$/.test(zipCode);
};

// Validation error messages
export const getValidationErrorMessage = (
  fieldName: string,
  validationType: string,
  options?: {
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
  }
): string => {
  const field = fieldName.toLowerCase();

  switch (validationType) {
    case "required":
      return `${fieldName} is required`;
    case "email":
      return "Please enter a valid email address";
    case "phone":
      return "Please enter a valid phone number";
    case "password":
      return "Password must be at least 8 characters and contain both letters and numbers";
    case "strongPassword":
      return "Password must be at least 8 characters with uppercase, lowercase, number, and special character";
    case "minLength":
      return `${fieldName} must be at least ${options?.minLength} characters`;
    case "maxLength":
      return `${fieldName} must not exceed ${options?.maxLength} characters`;
    case "number":
      return `${fieldName} must be a valid number`;
    case "positiveNumber":
      return `${fieldName} must be a positive number`;
    case "integer":
      return `${fieldName} must be a whole number`;
    case "date":
      return "Please enter a valid date";
    case "futureDate":
      return "Date must be in the future";
    case "pastDate":
      return "Date must be in the past";
    case "url":
      return "Please enter a valid URL";
    case "name":
      return "Please enter a valid name (letters only)";
    case "jobTitle":
      return "Job title can only contain letters, spaces, and basic punctuation (no numbers)";
    case "question":
      return "Question can only contain letters, numbers, spaces, and basic punctuation";
    case "alphanumeric":
      return `${fieldName} must contain only letters and numbers`;
    case "min":
      return `${fieldName} must be at least ${options?.min}`;
    case "max":
      return `${fieldName} must not exceed ${options?.max}`;
    default:
      return `Invalid ${field}`;
  }
};

// Comprehensive validation function
export interface ValidationRule {
  type: string;
  message?: string;
  value?: any;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
}

export const validateField = (
  value: any,
  rules: ValidationRule[],
  fieldName: string = "Field"
): string => {
  for (const rule of rules) {
    let isValid = true;

    switch (rule.type) {
      case "required":
        isValid = validateRequired(value);
        break;
      case "email":
        isValid = !value || validateEmail(value);
        break;
      case "phone":
        isValid = !value || validatePhone(value);
        break;
      case "password":
        isValid = !value || validatePassword(value);
        break;
      case "strongPassword":
        isValid = !value || validateStrongPassword(value);
        break;
      case "minLength":
        isValid = !value || validateMinLength(value, rule.minLength || 0);
        break;
      case "maxLength":
        isValid = !value || validateMaxLength(value, rule.maxLength || 0);
        break;
      case "number":
        isValid = !value || validateNumber(value);
        break;
      case "positiveNumber":
        isValid = !value || validatePositiveNumber(value);
        break;
      case "integer":
        isValid = !value || validateInteger(value);
        break;
      case "date":
        isValid = !value || validateDate(value);
        break;
      case "futureDate":
        isValid = !value || validateFutureDate(value);
        break;
      case "pastDate":
        isValid = !value || validatePastDate(value);
        break;
      case "url":
        isValid = !value || validateURL(value);
        break;
      case "name":
        isValid = !value || validateName(value);
        break;
      case "jobTitle":
        isValid = !value || validateJobTitle(value);
        break;
      case "question":
        isValid = !value || validateQuestion(value);
        break;
      case "alphanumeric":
        isValid = !value || validateAlphanumeric(value);
        break;
      case "min":
        isValid =
          !value || (validateNumber(value) && Number(value) >= (rule.min || 0));
        break;
      case "max":
        isValid =
          !value || (validateNumber(value) && Number(value) <= (rule.max || 0));
        break;
      default:
        isValid = true;
    }

    if (!isValid) {
      return (
        rule.message ||
        getValidationErrorMessage(fieldName, rule.type, {
          minLength: rule.minLength,
          maxLength: rule.maxLength,
          min: rule.min,
          max: rule.max,
        })
      );
    }
  }

  return "";
};
